import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactCta } from "@/components/common/DetailPage";
import { imageFor } from "@/lib/imagery";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const POSITIONING_KEYS = [
  "positioning",
  "jurisdiction",
  "sectors",
  "compliance",
] as const;

const POSITIONING: Record<
  "en" | "it",
  Array<{ eyebrow: string; title: string; body: string; link?: { to: RouteKey; label: string } }>
> = {
  en: [
    {
      eyebrow: "01 — Positioning",
      title: "Italian international trading company.",
      body: "BNM S.r.l. acts as a commercial counterparty for the physical trade of grains, food products and petrochemicals, connecting qualified suppliers with institutional buyers.",
    },
    {
      eyebrow: "02 — Jurisdiction",
      title: "Registered under Italian law.",
      body: "BNM S.r.l. is a company registered in Italy, subject to the Italian Civil Code, EU regulatory framework and international trade conventions. Its corporate records are publicly verifiable.",
      link: { to: "compliance.corporate", label: "Corporate Information" },
    },
    {
      eyebrow: "03 — Sectors",
      title: "Three commodity verticals, one counterparty.",
      body: "Grains & cereals, food products and petrochemicals — traded and supplied through a single commercial and documentary interface, with sector-specific process discipline.",
      link: { to: "commodities.index", label: "See sectors" },
    },
    {
      eyebrow: "04 — Compliance",
      title: "A compliance-led commercial approach.",
      body: "Counterparty due diligence, a published KYC / verification workflow and an anti-fraud notice apply to every commercial engagement, before any agreement is finalised.",
      link: { to: "compliance.index", label: "Compliance & Trust" },
    },
  ],
  it: [
    {
      eyebrow: "01 — Posizionamento",
      title: "Società italiana di trading internazionale.",
      body: "BNM S.r.l. agisce come controparte commerciale per il commercio fisico di cereali, prodotti alimentari e petrolchimici, mettendo in contatto fornitori qualificati e buyer istituzionali.",
    },
    {
      eyebrow: "02 — Giurisdizione",
      title: "Registrata secondo il diritto italiano.",
      body: "BNM S.r.l. è una società registrata in Italia, soggetta al Codice Civile, al quadro regolatorio UE e alle convenzioni internazionali del commercio. I dati societari sono verificabili pubblicamente.",
      link: { to: "compliance.corporate", label: "Informazioni Societarie" },
    },
    {
      eyebrow: "03 — Settori",
      title: "Tre verticali di commodity, un'unica controparte.",
      body: "Cereali, prodotti alimentari e petrolchimici — trattati e forniti attraverso un'unica interfaccia commerciale e documentale, con disciplina di processo settoriale.",
      link: { to: "commodities.index", label: "Vedi i settori" },
    },
    {
      eyebrow: "04 — Compliance",
      title: "Un approccio commerciale orientato alla compliance.",
      body: "La due diligence delle controparti, un workflow KYC / verifica pubblicato e un avviso anti-frode si applicano a ogni engagement commerciale, prima della finalizzazione di qualsiasi accordo.",
      link: { to: "compliance.index", label: "Compliance" },
    },
  ],
};

const OFFICIAL: Record<"en" | "it", Array<{ key: string; label: string; body: string }>> = {
  en: [
    {
      key: "profile",
      label: "Corporate profile",
      body: "BNM S.R.L. is an Italian international trading company specialising in international trade, import and export, global sourcing, commodity supply and commercial consulting. Primary sectors: grains & cereals, food products and petrochemical products.",
    },
    {
      key: "governance",
      label: "Governance",
      body: "BNM S.R.L. is a Società a Responsabilità Limitata registered with the Camera di Commercio Toscana Nord-Ovest (REA PI-258592), with a share capital of €10,000.00 fully paid. Chairman: Bahram Nikmahzar. Board Member: Morteza Nikmahzar.",
    },
    {
      key: "scope",
      label: "Operational scope",
      body: "The company operates internationally with business activities across the European Union, the United Kingdom and selected international markets. Customs identifiers: EU EORI IT06654120481 · UK EORI GB047109919000.",
    },
  ],
  it: [
    {
      key: "profile",
      label: "Profilo societario",
      body: "BNM S.R.L. è una società italiana di trading internazionale specializzata in commercio internazionale, import ed export, global sourcing, fornitura di commodities e consulenza commerciale. Settori principali: cereali, prodotti alimentari e prodotti petrolchimici.",
    },
    {
      key: "governance",
      label: "Governance",
      body: "BNM S.R.L. è una Società a Responsabilità Limitata iscritta presso la Camera di Commercio Toscana Nord-Ovest (REA PI-258592), con capitale sociale di € 10.000,00 interamente versato. Presidente: Bahram Nikmahzar. Consigliere: Morteza Nikmahzar.",
    },
    {
      key: "scope",
      label: "Ambito operativo",
      body: "La società opera a livello internazionale con attività commerciali nell'Unione Europea, nel Regno Unito e in mercati internazionali selezionati. Identificativi doganali: EORI UE IT06654120481 · EORI Regno Unito GB047109919000.",
    },
  ],
};

export default function About() {
  const { t, path, locale } = useT();
  const img = imageFor("about");
  const items = POSITIONING[locale];

  return (
    <>
      <SEO routeKey="about" />
      <PageHero routeKey="about" imageUrl={img?.url} imageAlt={img?.alt} />

      {/* Positioning grid — 4 structural attributes, no invented copy */}
      <section className="border-b border-bone-500" aria-labelledby="about-positioning">
        <div className="container-bnm py-20 md:py-28">
          <h2 id="about-positioning" className="sr-only">
            {locale === "it" ? "Posizionamento" : "Positioning"}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-bone-500">
            {items.map((it, idx) => (
              <li
                key={POSITIONING_KEYS[idx]}
                className="border-r border-b border-bone-500 p-8 md:p-12 flex flex-col gap-5"
                data-testid={`about-${POSITIONING_KEYS[idx]}`}
              >
                <p className="eyebrow">{it.eyebrow}</p>
                <h3 className="font-display font-light text-2xl md:text-3xl leading-tight text-ink">
                  {it.title}
                </h3>
                <p className="text-slate2 leading-relaxed text-[15px] max-w-[52ch]">
                  {it.body}
                </p>
                {it.link && (
                  <Link
                    to={path(it.link.to)}
                    className="mt-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-ink hover:text-clay transition-colors border-b border-ink hover:border-clay pb-0.5 self-start"
                    data-testid={`about-link-${POSITIONING_KEYS[idx]}`}
                  >
                    {it.link.label}
                    <ArrowRight size={12} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Statutory / official-content section — verified company data */}
      <section className="border-b border-bone-500 bg-bone-300/40" aria-labelledby="about-official">
        <div className="container-bnm py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">05 — {t.sections.overviewLabel}</p>
            <h2 id="about-official" className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
              {locale === "it" ? "Contenuto ufficiale" : "Official content"}
            </h2>
            <p className="mt-6 text-xs uppercase tracking-eyebrow font-mono text-slate2 max-w-[38ch]">
              {COMPANY.legalName} · P. IVA {COMPANY.vat} · REA {COMPANY.rea}
            </p>
          </div>
          <div className="lg:col-span-8 flex flex-col gap-10">
            {OFFICIAL[locale].map((block) => (
              <div
                key={block.key}
                className="border-l-2 border-ink pl-6 py-2"
                data-testid={`about-official-${block.key}`}
              >
                <p className="eyebrow mb-2">{block.label}</p>
                <p className="text-ink text-lg md:text-xl font-display font-light leading-snug max-w-prose">
                  {block.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
